import React, { useState, useCallback, useEffect, useRef } from 'react';
import type { FC, ReactElement } from 'react';
import { styled } from '@compiled/react';
import { Button, Link } from '@helpcenter/analytics-components';
import { ChangeBoardingSingleSpotlight } from '@helpcenter/change-boarding-single-spotlight';
import {
	EngagementMessages,
	HELP_CENTERS_MANAGEMENT_BASE_PATH,
	EngagementMessagesTarget,
	RouteName,
	ScreenName,
} from '@helpcenter/constants';
import { EngagementCoordinationClient } from '@helpcenter/engagement-coordination-client';
import ErrorBoundary from '@helpcenter/error-boundary/src/error-boundary';
import {
	isCustomiseDropDownChangeBoardingEnabled,
	isCustomPagesUiEnabled,
	isCortopv2ExperimentEnabledWithExposure,
} from '@helpcenter/feature-flags';
import { getCloudId } from '@helpcenter/meta';
import * as grid from '@helpcenter/styles/src/grid';
import { sizes } from '@helpcenter/styles/src/sizes-viewport';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { useRouter } from 'react-resource-router';
import type { Column } from 'rest/requests-column-settings';
import { useSetCustomiseSidebarTrigger } from 'view/common/pushing-customize-sidebar';
import { useQueryParams } from 'view/search/common';
import { useAnalyticsEvents, AnalyticsContext } from '@atlaskit/analytics-next';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import MediaServicesBrushIcon from '@atlaskit/icon-lab/core/paint-brush';
import Lozenge from '@atlaskit/lozenge/lozenge';
import { CustomItem, MenuGroup, Section } from '@atlaskit/menu';
import { SpotlightManager, SpotlightTarget } from '@atlaskit/onboarding';
import Popup from '@atlaskit/popup';
import type { TriggerProps } from '@atlaskit/popup';
import { Box, Pressable, xcss } from '@atlaskit/primitives';
import { layers } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { FireUIAnalytics } from '@atlassian/analytics-bridge';
import { UI_EVENT_TYPE } from '@atlassian/analytics-web-react';
import type { Edition } from '@atlassian/edition-benefits-modal';
import { PremiumBenefitModalTrigger } from '@atlassian/edition-benefits-modal';
import { MODAL_CONFIG } from '@atlassian/edition-benefits-modal/constants';
import { editionBenefitsModalEntryPoint } from '@atlassian/edition-benefits-modal/edition-benefits-modal-entrypoint';
import { ModalContextProvider } from '@atlassian/entry-points/modal-context-provider';
import { ModalTrigger } from '@atlassian/entry-points/modal-trigger';
import {
	isAdvancedHelpCenter,
	isCSMHelpCenter,
} from '@atlassian/help-center-common-util/advanced-help-center';
import type { Env } from '@atlassian/help-center-common-util/env';
import { getEnv, getIsHCCustomizationEnabled } from '@atlassian/help-center-common-util/env';
import { getRelativeBasePath } from '@atlassian/help-center-common-util/history';
import { DynamicThemingButton } from '../dynamic-theming-button';

import messages from './messages';

interface Portal {
	id?: number;
	name?: string;
	isProjectSimplified: boolean;
	isProjectAdmin: boolean;
	canEditAnnouncement: boolean;
}
export interface StateProps {
	portal?: Portal;
	isOpen?: boolean;
	canAdministerJIRA: boolean;
	hasSiteAdministerAccess?: boolean;
	canCustomiseHelpCenter?: boolean;
	defaultColumnSettings?: Column[];
	jsmAIConfigEnabled?: boolean;
	disableCustomiseMenu?: boolean;
	projectKey?: string;
}

export interface DispatchProps {
	openHelpCenterCustomizationSidebar: () => void;
	openRequestListCustomizationSidebar: () => void;
}

export type Props = StateProps & DispatchProps;

export interface CustomizeMenuItem {
	link?: string;
	label: string;
	actionSubjectId: string;
	isVisible: boolean | undefined;
	customRenderer?: FC<{ menu: CustomizeMenuItem }>;
	onClick?: (event: React.MouseEvent | React.KeyboardEvent) => void;
	testId?: string;
	iconAfter?: ReactElement;
	isAnchor?: boolean;
	showSpotlight?: boolean;
}

// eslint-disable-next-line @atlassian/ssr-linting/no-dom-globals-in-module-scope
const isSmallScreen = window.innerWidth < sizes.xsmall;

const CustomPressable = ({
	children,
	...rest
}: {
	children?: React.ReactNode;
	onClick?: () => void;
}) => {
	return (
		<Pressable componentName="link" xcss={customPressableStyles} {...rest}>
			{children}
		</Pressable>
	);
};

/**
 * Feature gate: premium_benefits_modal_config_driven
 *
 * When enabled, open the new config-driven edition-benefits-modal via its async
 * entrypoint (`editionBenefitsModalEntryPoint` + `ModalTrigger`) instead of the
 * legacy `PremiumBenefitModalTrigger`. The entrypoint loads on demand, keeping
 * the modal's assets out of the initial bundle.
 *
 * Rollout: Pending — gate created 2026-07-15, default off
 * Target: TBD
 * Owner: Sequoia (SQA-4082)
 */
const PREMIUM_BENEFITS_MODAL_CONFIG_DRIVEN = 'premium_benefits_modal_config_driven';
const isPremiumBenefitsModalConfigDrivenEnabled = (): boolean =>
	FeatureGates.checkGate(PREMIUM_BENEFITS_MODAL_CONFIG_DRIVEN);

const PremiumBenefitsModalRenderer = ({ menu }: { menu: CustomizeMenuItem }) => {
	di(useAnalyticsEvents);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isConfigDriven = isPremiumBenefitsModalConfigDrivenEnabled();
	const edition = ((getEnv() as Env & { edition?: string }).edition ?? 'unknown') as
		| Edition
		| 'unknown';

	// The config-driven ModalTrigger path doesn't emit the trigger analytics that the
	// legacy PremiumBenefitModalTrigger did, so replicate them here for parity.
	const fireTriggerEvent = useCallback(
		(action: 'viewed' | 'clicked') =>
			createAnalyticsEvent({
				action,
				eventType: 'ui',
				actionSubject: 'modalTrigger',
				actionSubjectId: 'editionBenefitsModalTrigger',
				attributes: {
					edition,
					touchpointId: 'portal-customise-menu-edit-page-layout',
					product: 'jira-service-management',
					variantId: 'help-center-customization',
					packageName: '@atlassian/edition-benefits-modal',
					source: 'editionBenefitsModal',
				},
			}).fire('growth'), // FabricChannel.growth
		[createAnalyticsEvent, edition],
	);

	const hasFiredViewed = useRef(false);
	useEffect(() => {
		// Fire the trigger "viewed" event once when the config-driven trigger is shown.
		if (isConfigDriven && !hasFiredViewed.current) {
			hasFiredViewed.current = true;
			fireTriggerEvent('viewed');
		}
	}, [isConfigDriven, fireTriggerEvent]);

	const renderTrigger = ({
		ref,
		onTriggerClick,
		props: triggerProps,
	}: {
		ref: React.RefObject<HTMLElement>;
		props: object;
		onTriggerClick?: () => void;
	}) => (
		<Box ref={ref} {...triggerProps}>
			<AnalyticsContext data={{ actionSubjectId: menu.actionSubjectId }}>
				<CustomItem
					component={CustomPressable}
					// Compose the trigger analytics with the item's own handler so the "clicked"
					// event fires on the element itself rather than relying on event bubbling.
					onClick={
						onTriggerClick
							? (event: React.MouseEvent | React.KeyboardEvent) => {
									onTriggerClick();
									menu.onClick?.(event);
								}
							: menu.onClick
					}
					testId={menu.testId}
					iconAfter={menu.iconAfter}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve
					className="cv-exclude-from-customisation"
				>
					{menu.label}
				</CustomItem>
			</AnalyticsContext>
		</Box>
	);

	return (
		<ErrorBoundary
			packageName={ScreenName.HELP_CENTER}
			id="help-center-customise-menu-edit-page-layout"
			renderError={() => null}
		>
			{isConfigDriven ? (
				<ModalTrigger
					entryPoint={editionBenefitsModalEntryPoint}
					entryPointParams={{ cloudId: getCloudId(), product: 'jira-service-management' }}
					entryPointProps={{
						variantId: 'help-center-customization',
						touchpointId: 'portal-customise-menu-edit-page-layout',
					}}
					modalProps={MODAL_CONFIG}
				>
					{({ ref }) =>
						renderTrigger({ ref, props: {}, onTriggerClick: () => fireTriggerEvent('clicked') })
					}
				</ModalTrigger>
			) : (
				<PremiumBenefitModalTrigger
					cloudId={getCloudId()}
					product="jira-service-management"
					variantId="help-center-customization"
					touchpointId="portal-customise-menu-edit-page-layout"
					edition={edition}
				>
					{renderTrigger}
				</PremiumBenefitModalTrigger>
			)}
		</ErrorBoundary>
	);
};

const CustomizeLinks = (
	onClose: (actionSubjectId?: string) => void,
	openHelpCenterCustomizationSidebar: DispatchProps['openHelpCenterCustomizationSidebar'],
	canAdministerJIRA: boolean,
	showEditLayoutButton: boolean,
	isHomePage: boolean,
	basePath: string,
	handleHideSpotlight: () => void,
) => {
	di(
		EngagementCoordinationClient,
		isCustomiseDropDownChangeBoardingEnabled,
		getIsHCCustomizationEnabled,
		isCSMHelpCenter,
		isCustomPagesUiEnabled,
		useAnalyticsEvents,
	);
	const { formatMessage } = useIntl();
	const isCsmHelpCenter = isCSMHelpCenter(getEnv().helpCenterType);
	const showPortalReorderButton =
		!isCsmHelpCenter && isHomePage && canAdministerJIRA && !isSmallScreen;

	const customizeMenu: CustomizeMenuItem[] = [
		{
			link: `${basePath}?customize=true&experience=help-center-announcement`,
			label: formatMessage(messages.announcement),
			actionSubjectId: 'navbarAnnouncement',
			isVisible: isHomePage && !isSmallScreen,
			onClick: () => onClick('navbarAnnouncement'),
			testId: 'announcement-link',
		},
		{
			link: `${basePath}?customize=true&experience=theme`,
			label: formatMessage(messages.look),
			actionSubjectId: 'navbarLookAndFeel',
			isVisible: isHomePage && canAdministerJIRA,
			onClick: () => onClick('navbarLookAndFeel'),
			testId: 'look-and-feel-link',
		},
		{
			link: `${basePath}?customize=true&experience=login-announcement`,
			label: formatMessage(messages.logInMessage),
			actionSubjectId: 'navbarLoginAnnouncement',
			isVisible: isHomePage && canAdministerJIRA && !isSmallScreen,
			onClick: () => onClick('navbarLoginAnnouncement'),
			testId: 'login-announcement-link',
		},
		{
			link: '/settings/portal-reorder',
			label: formatMessage(messages.topicsAndPortals),
			actionSubjectId: 'navbarPortalReorder',
			isVisible: showPortalReorderButton,
			onClick: () => onClick('navbarPortalReorder'),
			testId: 'topics-link',
		},
		{
			link: '/edit',
			label: formatMessage(messages.pageLayout),
			actionSubjectId: 'navbarHideAndReorder',
			isVisible: showEditLayoutButton,
			onClick: () => onClick('navbarHideAndReorder', !getIsHCCustomizationEnabled()),
			testId: 'hide-and-reorder-link',
			showSpotlight: true,
			customRenderer: getIsHCCustomizationEnabled() ? undefined : PremiumBenefitsModalRenderer,
			iconAfter: (
				<>
					{getIsHCCustomizationEnabled() ? undefined : (
						<Lozenge appearance="discovery">{formatMessage(messages.premium)}</Lozenge>
					)}
				</>
			),
		},
	];

	const onClick = (actionSubjectId: string, closeSidebar?: boolean) => {
		onClose(actionSubjectId);
		if (!closeSidebar) {
			openHelpCenterCustomizationSidebar();
		}
	};
	return (
		<Section>
			{customizeMenu.map((menu) => {
				if (!menu.isVisible) {
					return null;
				}
				if (menu.customRenderer) {
					return <menu.customRenderer key={`customize-menu_${menu.actionSubjectId}`} menu={menu} />;
				}
				return (
					<>
						<SpotlightTarget
							name={EngagementMessagesTarget.CHANGE_BOARDING_CUSTOMISE_BUTTON_SPOTLIGHT}
						>
							<CustomItem
								key={`customize-menu_${menu.actionSubjectId}`}
								component={CustomLink}
								to={menu.link}
								actionSubjectId={menu.actionSubjectId}
								onClick={menu.onClick}
								testId={menu.testId}
								iconAfter={menu.iconAfter}
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve
								className="cv-exclude-from-customisation"
							>
								{menu.label}
							</CustomItem>
						</SpotlightTarget>
						{menu.showSpotlight && isCustomiseDropDownChangeBoardingEnabled() && (
							<EngagementCoordinationClient
								messageId={EngagementMessages.CHANGE_BOARD_CUSTOMISE_BUTTON}
							>
								<ChangeBoardingSingleSpotlight
									target={EngagementMessagesTarget.CHANGE_BOARDING_CUSTOMISE_BUTTON_SPOTLIGHT}
									analyticsSubject="customiseButtonChangeBoarding"
									messageId={EngagementMessages.CHANGE_BOARD_CUSTOMISE_BUTTON}
									contentText={formatMessage(messages.changeBoardingText)}
									buttonText={formatMessage(messages.changeBoardingButtonText)}
									dialogPlacement="bottom right"
									targetBgColor={token('elevation.surface.raised', '#FFFFFF')}
									onHideSpotlight={handleHideSpotlight}
								/>
							</EngagementCoordinationClient>
						)}
					</>
				);
			})}
		</Section>
	);
};

const PortalLinks = ({
	onClose,
	canCustomiseHelpCenter,
	portal,
	projectKey,
}: {
	onClose: (actionSubjectId?: string) => void;
	canCustomiseHelpCenter: boolean | undefined;
	portal: Portal | undefined;
	projectKey?: string;
}) => {
	di(isCortopv2ExperimentEnabledWithExposure);

	const { formatMessage } = useIntl();
	if (!portal) {
		return null;
	}
	const { isProjectSimplified, isProjectAdmin, canEditAnnouncement } = portal;
	const shouldRenderAsPortalAnnouncement = (): boolean => {
		// for simplified projects, render side bar as announcement for agents with permission
		if (isProjectSimplified && !isProjectAdmin && canEditAnnouncement) {
			return true;
		}
		// in classic projects render sidebar as announcement for users who can't modify helpcenter, but can edit announcements
		if (!isProjectSimplified && !canCustomiseHelpCenter && canEditAnnouncement) {
			return true;
		}
		return false;
	};

	const shouldRenderConfigurePortal = () =>
		isProjectAdmin &&
		!isProjectSimplified &&
		projectKey?.length &&
		isCortopv2ExperimentEnabledWithExposure(true);

	const shouldRenderCustomizeRequestTypes = () =>
		isProjectAdmin &&
		!isProjectSimplified &&
		projectKey?.length &&
		isCortopv2ExperimentEnabledWithExposure(true);

	const shouldRenderCustomizePortalGroups = () =>
		isProjectAdmin &&
		!isProjectSimplified &&
		projectKey?.length &&
		isCortopv2ExperimentEnabledWithExposure(true);

	const customizeMenu = [
		{
			link: '?customize=true&experience=add-announcement',
			onClick: () => onClose('navbarPortalAnnouncement'),
			label: formatMessage(messages.portalAnnouncement),
			actionSubjectId: 'navbarPortalAnnouncement',
			isVisible: canEditAnnouncement || shouldRenderAsPortalAnnouncement(),
		},
		{
			link: '?customize=true&experience=request-groups',
			onClick: () => onClose('navbarRequestTypeGroup'),
			label: formatMessage(messages.portalRequestTypeGroup),
			actionSubjectId: 'navbarRequestTypeGroup',
			isVisible: isProjectAdmin && isProjectSimplified && !shouldRenderAsPortalAnnouncement(),
		},
		{
			// eslint-disable-next-line rulesdir/do-not-manually-build-jsm-agent-view-url
			href: `/jira/servicedesk/projects/${projectKey ?? ''}/settings/portal-settings`,
			onClick: () => onClose('navbarConfigurePortal'),
			label: formatMessage(messages.configurePortal),
			actionSubjectId: 'navbarConfigurePortal',
			isVisible: shouldRenderConfigurePortal(),
			target: '_blank',
		},
		{
			// eslint-disable-next-line rulesdir/do-not-manually-build-jsm-agent-view-url
			href: `/jira/servicedesk/projects/${projectKey ?? ''}/settings/${
				isProjectSimplified ? 'issuetypes' : 'request-types'
			}/`,
			onClick: () => onClose('navbarCustomizeRequestTypes'),
			label: formatMessage(messages.customizeRequestTypes),
			actionSubjectId: 'navbarCustomizeRequestTypes',
			isVisible: shouldRenderCustomizeRequestTypes(),
			target: '_blank',
		},
		{
			// eslint-disable-next-line rulesdir/do-not-manually-build-jsm-agent-view-url
			href: `/jira/servicedesk/projects/${projectKey ?? ''}/settings/portal-settings/portal-groups`,
			onClick: () => onClose('navbarManagePortalGroups'),
			label: formatMessage(messages.managePortalGroups),
			actionSubjectId: 'navbarManagePortalGroups',
			isVisible: shouldRenderCustomizePortalGroups(),
			target: '_blank',
		},
	];

	return (
		<Section>
			{customizeMenu.map(({ isVisible, actionSubjectId, onClick, link, href, label, target }) => {
				if (!isVisible) {
					return null;
				}
				return (
					<CustomItem
						key={`portal-link_${actionSubjectId}`}
						component={CustomLink}
						actionSubjectId={actionSubjectId}
						onClick={onClick}
						{...(link ? { to: link } : {})}
						{...(href ? { href } : {})}
						testId={`portal-link_${actionSubjectId}`}
						// Made this check of menu.target === '_blank' to avoid the eslint error
						target={target === '_blank' ? '_blank' : '_self'}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve
						className="cv-exclude-from-customisation"
					>
						{label}
					</CustomItem>
				);
			})}
		</Section>
	);
};

const CustomPageLinks = ({ showEditCustomPages }: { showEditCustomPages: boolean }) => {
	di(useIntl, useRouter, isCustomPagesUiEnabled);
	const { formatMessage } = useIntl();
	const [{ match }] = useRouter();
	const pageId: string = match.params.id || '';
	const customizeMenu = [
		{
			link: `/page/edit/${pageId}`,
			label: formatMessage(messages.pageLayout),
			actionSubjectId: 'navbarEditCustomPage',
			isVisible: showEditCustomPages,
		},
	];

	return (
		<Section>
			{customizeMenu.map((menu) => {
				if (!menu.isVisible) {
					return null;
				}
				return (
					<CustomItem
						key={`custom-page-link_${menu.actionSubjectId}`}
						component={CustomLink}
						to={menu.link}
						actionSubjectId={menu.actionSubjectId}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve
						className="cv-exclude-from-customisation"
					>
						{menu.label}
					</CustomItem>
				);
			})}
		</Section>
	);
};

const RequestList = (
	onClose: (actionSubjectId?: string) => void,
	openRequestListCustomizationSidebar: DispatchProps['openRequestListCustomizationSidebar'],
) => {
	const onClick = (actionSubjectId: string) => {
		onClose(actionSubjectId);
		openRequestListCustomizationSidebar();
	};
	const { formatMessage } = useIntl();
	return (
		<Section>
			<CustomItem
				component={CustomButton}
				actionSubjectId="navbarUserRequestListCustomize"
				aria-label={formatMessage(messages.customizeRequestList)}
				onClick={() => onClick('navbarUserRequestListCustomize')}
				data-test-id="requests-list-page-link"
			>
				{formatMessage(messages.customizeRequestList)}
			</CustomItem>
		</Section>
	);
};

const CustomiseMenu = (props: Props) => {
	di(
		useAnalyticsEvents,
		getRelativeBasePath,
		isAdvancedHelpCenter,
		useRouter,
		isCSMHelpCenter,
		isCustomPagesUiEnabled,
		isCortopv2ExperimentEnabledWithExposure,
	);
	const [{ route }] = useRouter();
	const { name: routeName } = route;
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	const { customize, experience } = useQueryParams();

	useEffect(() => {
		// if customize is true and experience is not set, open the customise menu
		if (customize === 'true' && !experience) {
			setIsOpen(true);
		}
	}, [customize, experience]);

	const isHomepage = routeName === 'help-center' || routeName === 'view-homepage';
	const isCustomPage = routeName === RouteName.VIEW_CUSTOM_PAGE;
	const showEditCustomPageButton =
		isCustomPagesUiEnabled() && props.canAdministerJIRA && !isSmallScreen && isCustomPage;

	const showEditLayoutButtonUpsell =
		getIsHCCustomizationEnabled() || (props.hasSiteAdministerAccess ?? false);
	const showEditLayoutButton =
		isHomepage && props.canAdministerJIRA && !isSmallScreen && showEditLayoutButtonUpsell;
	const isHelpCenterManagementScreen = getRelativeBasePath() === HELP_CENTERS_MANAGEMENT_BASE_PATH;
	const basePath = isAdvancedHelpCenter() ? '/' : '/portals';
	const [isOpen, setIsOpen] = useState(!!props.isOpen);

	const { formatMessage } = useIntl();

	const handleHideSpotlight = useCallback(() => {
		setIsOpen(false);
	}, []);

	const onOpenChange = useCallback((open: boolean) => {
		setIsOpen(open);
	}, []);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const setCustomiseSidebarTrigger = useSetCustomiseSidebarTrigger();

	const onItemClick = (actionSubjectId?: string) => {
		if (actionSubjectId && createAnalyticsEvent) {
			createAnalyticsEvent({
				actionSubjectId,
				action: 'clicked',
				actionSubject: 'customizeMenu',
				analyticsType: UI_EVENT_TYPE,
			}).fire();
		}
		setIsOpen(false);
	};

	const allowRouteList = [
		'help-center',
		'view-homepage',
		'portal-home',
		'requests-list',
		...[isCustomPagesUiEnabled() ? RouteName.VIEW_CUSTOM_PAGE : undefined],
	];

	const hideCustomizeButton = () =>
		!allowRouteList.includes(routeName) ||
		isHelpCenterManagementScreen ||
		(isHomepage && !props.canCustomiseHelpCenter) ||
		//Hide for Agents with Edit Permission in mobile view
		(isHomepage && props.canCustomiseHelpCenter && !props.canAdministerJIRA && isSmallScreen) ||
		(routeName === 'portal-home' &&
			!(props.portal?.isProjectAdmin || props.portal?.canEditAnnouncement)) ||
		(routeName === 'requests-list' && !props.canAdministerJIRA) ||
		(isCustomPagesUiEnabled() && isCustomPage && !props.canAdministerJIRA);

	const content = () => {
		const {
			openHelpCenterCustomizationSidebar,
			openRequestListCustomizationSidebar,
			canAdministerJIRA,
			canCustomiseHelpCenter,
			defaultColumnSettings,
			portal,
			projectKey,
		} = props;
		return (
			<Box
				xcss={popupContainerStyles}
				aria-label={formatMessage(messages.customizeAriaLabel)}
				testId={'customize-menu-content'}
				role="group"
			>
				<MenuGroup maxHeight={maxHeight} spacing={'cozy'}>
					{!isHelpCenterManagementScreen && (
						<>
							{canCustomiseHelpCenter &&
								isHomepage &&
								CustomizeLinks(
									onItemClick,
									openHelpCenterCustomizationSidebar,
									canAdministerJIRA,
									showEditLayoutButton,
									isHomepage,
									basePath,
									handleHideSpotlight,
								)}
							{routeName === 'portal-home' && (
								<PortalLinks
									onClose={onItemClick}
									canCustomiseHelpCenter={canCustomiseHelpCenter}
									portal={portal}
									projectKey={projectKey}
								/>
							)}
							{routeName === 'requests-list' &&
								canAdministerJIRA &&
								Boolean(defaultColumnSettings?.length) &&
								RequestList(onItemClick, openRequestListCustomizationSidebar)}
							{isCustomPage && showEditCustomPageButton && (
								<CustomPageLinks showEditCustomPages={showEditCustomPageButton} />
							)}
						</>
					)}
				</MenuGroup>
				{showEditLayoutButton && <FireUIAnalytics action="viewed" actionSubject="editPageLayout" />}
			</Box>
		);
	};

	const Trigger = useCallback(
		({ ref, ...triggerProps }: Partial<TriggerProps>) => {
			const combinedRef = (element: HTMLElement | null) => {
				if (typeof ref === 'function') {
					ref(element);
				} else if (ref && typeof ref === 'object' && 'current' in ref) {
					(ref as React.MutableRefObject<HTMLElement | null>).current = element;
				}

				setCustomiseSidebarTrigger?.(element);
			};

			return (
				<div role="listitem">
					<DynamicThemingButton
						label={formatMessage(messages.customizeBtnLabel)}
						icon={<MediaServicesBrushIcon label="customise" size="small" spacing="spacious" />}
						disabled={props.disableCustomiseMenu}
						onClick={() => {
							onOpenChange(!isOpen);
							createAnalyticsEvent({
								action: 'clicked',
								actionSubject: 'button',
								actionSubjectId: 'navbarCustomise',
								analyticsType: UI_EVENT_TYPE,
							}).fire();
						}}
						forwardedRef={combinedRef}
						{...triggerProps}
					/>
				</div>
			);
		},
		[
			formatMessage,
			props.disableCustomiseMenu,
			onOpenChange,
			isOpen,
			createAnalyticsEvent,
			setCustomiseSidebarTrigger,
		],
	);

	const renderDropdownMenu = () => (
		<SpotlightManager blanketIsTinted>
			<ModalContextProvider>
				<Popup
					isOpen={isOpen}
					onClose={() => onOpenChange(false)}
					placement="bottom-end"
					shouldRenderToParent
					zIndex={layers.modal()}
					content={content}
					trigger={Trigger}
					key={'customise-menu-dumb-popup'}
				/>
			</ModalContextProvider>
		</SpotlightManager>
	);

	if (hideCustomizeButton()) {
		return null;
	}

	return <Box xcss={CustomiseBtnWrapperStyles}>{renderDropdownMenu()}</Box>;
};

const CustomiseBtnWrapperStyles = xcss({
	marginRight: 'space.100',
	marginLeft: 'space.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const NoUnderlineLink = styled(Link)({
	textDecoration: 'none',
	'&:focus, &:hover, &:visited, &:link, &:active': {
		textDecoration: 'none',
	},
});

const popupContainerStyles = xcss({
	minWidth: '234px',
	maxWidth: '800px',
	color: 'color.text',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const CustomLink = styled(NoUnderlineLink)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Disabled to rollout go/ui-styling-standard tooling, please resolve
	color: 'unset !important',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&&:focus-visible:focus': {
		outline: 'none',
		boxShadow: `inset 0px 0px 0px 2px ${token('color.border.focused', '#4C9AFF')}`,
	},
});

const customPressableStyles = xcss({
	color: 'color.text',
	backgroundColor: 'color.background.neutral.subtle',
	width: '100%',
	padding: 'space.100',
	paddingInline: 'space.200',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const CustomButton = styled(Button)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Disabled to rollout go/ui-styling-standard tooling, please resolve
	color: `${token('color.text', '#172B4D')} !important`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Disabled to rollout go/ui-styling-standard tooling, please resolve
	background: 'unset !important',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Disabled to rollout go/ui-styling-standard tooling, please resolve
	width: '100% !important',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Disabled to rollout go/ui-styling-standard tooling, please resolve
	alignItems: 'center !important',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Disabled to rollout go/ui-styling-standard tooling, please resolve
	padding: '8px 16px !important',
	'&:focus, &:hover, &:visited, &:link, &:active': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Disabled to rollout go/ui-styling-standard tooling, please resolve
		color: `${token('color.text', '#172B4D')} !important`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Disabled to rollout go/ui-styling-standard tooling, please resolve
		background: `${token('color.background.neutral.subtle.hovered', '#EBECF0')} !important`,
	},
});

const maxHeight = `calc(100vh - ${grid.multiple(24).px}`;

export default CustomiseMenu;
